import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  API_SCOPES,
  DEFAULT_ROLES,
  INDUSTRIES,
  INDUSTRY_LABELS,
  PLAN_LABELS,
  PLANS,
  TENANT_STATUS_LABELS,
  TENANT_STATUSES,
  TRIAL_DAYS,
  type ApiScope,
  type Industry,
  type InvitationResult,
  type ModuleKey,
  type OperatorApiKey,
  type OperatorLogEntry,
  type Plan,
  type TenantStatus,
  type TenantSummary, INCLUDED_USERS } from '@kontor/shared';
import { ApiError } from '../api';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { InvitationLink } from '../components/InvitationLink';
import { operatorApi } from './operatorApi';
import { AddonPicker, addonChoices, statusTone } from './OperatorTenantsPage';
import { useMeta } from '../meta';

const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/** Seite eines Mandanten: Anmeldung, Branche, Lizenz, ersten Inhaber einladen, API-Schlüssel und Protokoll (KON-092 bis KON-095, KON-241) */
export function OperatorTenantPage() {
  const { id = '' } = useParams();
  const tenant = useQuery({ queryKey: ['operator-tenant', id], queryFn: async () => (await operatorApi<{ data: TenantSummary }>('GET', `/tenants/${id}`)).data });

  if (tenant.isLoading) return <p className="muted">Wird geladen …</p>;
  if (tenant.isError || !tenant.data) {
    return (
      <div className="card narrow">
        <h1>Mandant nicht gefunden</h1>
        <Link to="/betreiber">Zur Liste</Link>
      </div>
    );
  }
  const t = tenant.data;
  return (
    <>
      <p><Link to="/betreiber" className="back-link">← Alle Mandanten</Link></p>
      <div className="page-head">
        <h1>{t.name}</h1>
        <span className={`badge ${statusTone(t.status)}`}>{TENANT_STATUS_LABELS[t.status]}</span>
      </div>
      <p className="muted">{t.seatsUsed} von {t.maxUsers} Benutzerplätzen belegt · {t.inquiryCount} {t.inquiryCount === 1 ? 'Anfrage' : 'Anfragen'}</p>

      <AccessCard tenant={t} />
      <IndustryCard tenant={t} />
      <LicenseCard tenant={t} />
      <TrialCard tenant={t} />
      <OwnerCard tenant={t} />
      <ApiKeysCard tenantId={t.id} />
      <LogCard tenantId={t.id} />
    </>
  );
}

/**
 * Branche des Mandanten (KON-241). Wirkia wird je Branche verkauft: Der Betreiber legt sie hier fest (bei der Bestellung auf der Webseite wählt sie der Kunde, bei der Testanmeldung steht sie im Formular),
 * der Mandant wählt sie nicht selbst. Das Festlegen führt den Ablauf der Branche aus: Anfragetypen, Einheiten, Rollen und Menü werden angelegt, was fehlt, nichts wird gelöscht oder überschrieben.
 * Mit der Branche stehen die Funktionen zur Verfügung, die nur dafür gelten (Chauffeur-Service: Auftragsbuch, Disposition, Fahrten annehmen). Wechselt die Branche, lässt Wirkia Angelegtes stehen, die
 * Funktionen der früheren Branche stehen dem Mandanten dann nicht mehr zur Verfügung.
 */
function IndustryCard({ tenant }: { tenant: TenantSummary }) {
  const client = useQueryClient();
  const [choice, setChoice] = useState<string>(tenant.industry ?? '');
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setChoice(tenant.industry ?? ''), [tenant.industry]);
  const save = useMutation({
    mutationFn: (industry: string | null) => operatorApi('PUT', `/tenants/${tenant.id}/industry`, { industry }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-tenant', tenant.id] });
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      client.invalidateQueries({ queryKey: ['operator-log', tenant.id] });
      setMessage({ ok: true, text: 'Die Branche ist festgelegt, die Einrichtung der Branche ist angewendet. Der Mandant sieht die Änderung, sobald er die Seite neu lädt.' });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Das Festlegen hat nicht geklappt.' }),
  });
  const changed = (choice || null) !== tenant.industry;
  return (
    <section className="card">
      <h2>Branche</h2>
      <p className="muted small">
        Die Branche bestimmt Menü, Rollen, Anfragetypen und die Funktionen, die nur für sie gelten. Der Mandant ändert sie nicht selbst.
        {tenant.industryAppliedAt ? ` Zuletzt angewendet am ${stamp.format(new Date(tenant.industryAppliedAt))} Uhr.` : ''}
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Branche</span>
          <select className="input" value={choice} onChange={(e) => setChoice(e.target.value)}>
            <option value="">Keine Branche (allgemein)</option>
            {INDUSTRIES.map((i) => <option key={i} value={i}>{INDUSTRY_LABELS[i]}</option>)}
          </select>
        </label>
      </div>
      <div className="form-actions">
        <button
          className="btn btn--primary"
          type="button"
          disabled={save.isPending || !changed}
          onClick={() => {
            if (tenant.industry && choice !== tenant.industry && !window.confirm(`Die Branche von „${tenant.name}“ von ${INDUSTRY_LABELS[tenant.industry as Industry]} auf ${choice ? INDUSTRY_LABELS[choice as Industry] : 'keine Branche'} ändern? Was nur für ${INDUSTRY_LABELS[tenant.industry as Industry]} gilt, steht dem Mandanten danach nicht mehr zur Verfügung. Angelegte Anfragetypen und Rollen bleiben.`)) return;
            setMessage(null);
            save.mutate(choice || null);
          }}
        >
          {save.isPending ? 'Wird festgelegt …' : 'Branche festlegen'}
        </button>
        {tenant.industry && !changed && (
          <button className="btn btn--secondary" type="button" disabled={save.isPending} onClick={() => { setMessage(null); save.mutate(tenant.industry); }}>
            Einrichtung erneut anwenden
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * Anmeldung beim Mandanten (KON-116): die Mandantennummer zum Weitergeben und im Übergang der Schalter für die alte Firmenkennung. Abschalten trifft nur die
 * Anmeldung, bestehende Sitzungen laufen weiter.
 */
function AccessCard({ tenant }: { tenant: TenantSummary }) {
  const client = useQueryClient();
  const [copied, setCopied] = useState(false);
  const toggle = useMutation({
    mutationFn: (legacyLogin: boolean) => operatorApi<{ data: TenantSummary }>('PATCH', `/tenants/${tenant.id}`, { legacyLogin }),
    onSuccess: (res) => {
      client.setQueryData(['operator-tenant', tenant.id], res.data);
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      client.invalidateQueries({ queryKey: ['operator-log', tenant.id] });
    },
  });
  return (
    <section className="card">
      <h2>Anmeldung</h2>
      <div className="operator__number">
        <div>
          <div className="muted small">Mandantennummer</div>
          <strong className="operator__number-value">{tenant.number}</strong>
        </div>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(tenant.number);
              setCopied(true);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? 'Kopiert' : 'Kopieren'}
        </button>
      </div>
      <p className="muted small">Die Benutzer melden sich mit dieser Nummer, ihrer E-Mail-Adresse und ihrem Passwort an. Sie steht auch in jeder Einladung.</p>
      {(tenant.legacyLogin || !tenant.slug.startsWith('m')) && (
        <div className="operator__legacy">
          <p>
            {tenant.legacyLogin ? (
              <>Übergang: Die Anmeldung nimmt noch die alte Firmenkennung <strong>„{tenant.slug}“</strong> an. Schalten Sie das ab, sobald alle Benutzer ihre Nummer kennen.</>
            ) : (
              <>Die Anmeldung mit der alten Firmenkennung „{tenant.slug}“ ist abgeschaltet.</>
            )}
          </p>
          <button
            type="button"
            className={`btn btn--sm ${tenant.legacyLogin ? 'btn--danger' : 'btn--secondary'}`}
            disabled={toggle.isPending}
            onClick={() => (!tenant.legacyLogin || window.confirm(`Anmeldung mit „${tenant.slug}“ abschalten?\n\nDanach geht es nur noch mit der Mandantennummer ${tenant.number}.`)) && toggle.mutate(!tenant.legacyLogin)}
          >
            {tenant.legacyLogin ? 'Alte Anmeldung abschalten' : 'Alte Anmeldung wieder erlauben'}
          </button>
          {toggle.error && <div className="alert alert--error" role="alert">{toggle.error.message}</div>}
        </div>
      )}
    </section>
  );
}

function LicenseCard({ tenant }: { tenant: TenantSummary }) {
  useMeta();
  const client = useQueryClient();
  const [name, setName] = useState(tenant.name);
  const [plan, setPlan] = useState<Plan>(tenant.plan);
  const [addons, setAddons] = useState<ModuleKey[]>(tenant.addons);
  const [maxUsers, setMaxUsers] = useState(tenant.maxUsers);
  const [validUntil, setValidUntil] = useState(tenant.validUntil ?? '');
  const [status, setStatus] = useState<TenantStatus>(tenant.status);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  // Nach dem Speichern oder Neuladen die Felder auf den gespeicherten Stand setzen
  useEffect(() => {
    setName(tenant.name);
    setPlan(tenant.plan);
    setAddons(tenant.addons);
    setMaxUsers(tenant.maxUsers);
    setValidUntil(tenant.validUntil ?? '');
    setStatus(tenant.status);
  }, [tenant]);

  const save = useMutation({
    mutationFn: () => operatorApi<{ data: TenantSummary }>('PATCH', `/tenants/${tenant.id}`, { name: name.trim(), plan, addons, maxUsers, validUntil: validUntil || null, status }),
    onSuccess: (res) => {
      client.setQueryData(['operator-tenant', tenant.id], res.data);
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      client.invalidateQueries({ queryKey: ['operator-log', tenant.id] });
      setErrors({});
      setMessage({ ok: true, text: 'Gespeichert.' });
    },
    onError: (e) => {
      setErrors(e instanceof ApiError && e.status === 422 ? e.fields : {});
      setMessage({ ok: false, text: e instanceof ApiError ? (e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message) : 'Die Änderung konnte nicht gespeichert werden.' });
    },
  });

  const dirty = name.trim() !== tenant.name || plan !== tenant.plan || JSON.stringify([...addons].sort()) !== JSON.stringify([...tenant.addons].sort()) || maxUsers !== tenant.maxUsers || validUntil !== (tenant.validUntil ?? '') || status !== tenant.status;

  function submit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    // Sperren und Kündigen trifft sofort alle Benutzer des Mandanten, deshalb fragt die Oberfläche nach
    if (status !== tenant.status && (status === 'suspended' || status === 'cancelled' || status === 'expired') && !window.confirm(`Mandant „${tenant.name}“ wirklich auf „${TENANT_STATUS_LABELS[status]}“ setzen?\n\nAlle Benutzer und alle API-Schlüssel des Mandanten verlieren sofort den Zugang.`)) return;
    save.mutate();
  }
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <form className="card" onSubmit={submit} noValidate>
      <h2>Lizenz</h2>
      {tenant.status === 'trial' && <p className="muted">Testmandant. Wird der Status auf „Aktiv“ gesetzt, entfällt das Ablaufdatum, und alle Daten bleiben erhalten.</p>}
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors.name} />
          {error('name')}
        </label>
        <label className="field">
          <span>Paket</span>
          <select className="input" value={plan} onChange={(e) => { setPlan(e.target.value as Plan); setAddons([]); }}>
            {PLANS.map((p) => <option key={p} value={p}>{PLAN_LABELS[p]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Benutzerplätze</span>
          <input className="input" type="number" min={tenant.subscribedSeats} max={1000} value={maxUsers} onChange={(e) => setMaxUsers(Number(e.target.value))} aria-invalid={!!errors.maxUsers} />
          {error('maxUsers')}
          <small className="muted">
            Der Mandant hat {tenant.subscribedSeats} {tenant.subscribedSeats === 1 ? 'Platz' : 'Plätze'} abonniert ({INCLUDED_USERS} enthalten{tenant.subscribedSeats > INCLUDED_USERS ? `, ${tenant.subscribedSeats - INCLUDED_USERS} gebucht` : ''}). Sie können mehr freigeben, aber nicht weniger setzen. Gebuchte Plätze kündigt der Mandant selbst unter Einrichtung → Wirkia Konto.
          </small>
        </label>
        <label className="field">
          <span>Gültig bis</span>
          <input className="input" type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} aria-invalid={!!errors.validUntil} />
          <small className="muted">Leer: unbefristet. Nach dem Ablauf kann sich niemand mehr anmelden.</small>
          {error('validUntil')}
        </label>
        <label className="field">
          <span>Status</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value as TenantStatus)}>
            {TENANT_STATUSES.map((s) => <option key={s} value={s}>{TENANT_STATUS_LABELS[s]}</option>)}
          </select>
        </label>
      </div>
      <AddonPicker choices={addonChoices(plan)} value={addons} onChange={setAddons} />
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending || !dirty}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
      </div>
    </form>
  );
}

/** Testphase einmal um 14 Tage verlängern. Auch ein gerade abgelaufener Test lässt sich damit wieder öffnen, solange er nicht gelöscht ist (KON-177). */
function TrialCard({ tenant }: { tenant: TenantSummary }) {
  const client = useQueryClient();
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const extend = useMutation({
    mutationFn: () => operatorApi<{ data: TenantSummary }>('POST', `/tenants/${tenant.id}/trial/extend`),
    onSuccess: (res) => {
      client.setQueryData(['operator-tenant', tenant.id], res.data);
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      client.invalidateQueries({ queryKey: ['operator-log', tenant.id] });
      setMessage({ ok: true, text: `Die Testphase läuft jetzt bis ${res.data.validUntil ?? ''}.` });
    },
    onError: (e) => setMessage({ ok: false, text: e instanceof ApiError ? e.message : 'Die Verlängerung hat nicht geklappt.' }),
  });
  if (tenant.status !== 'trial' && tenant.status !== 'expired') return null;
  return (
    <section className="card">
      <h2>Testphase</h2>
      <p className="muted">
        {tenant.status === 'expired' ? 'Die Testphase ist abgelaufen, der Zugang ist gesperrt.' : `Die Testphase läuft bis ${tenant.validUntil ?? ''}.`}{' '}
        {tenant.trialExtended ? 'Sie wurde schon einmal verlängert, eine weitere Verlängerung ist nicht vorgesehen.' : `Sie lässt sich einmal um ${TRIAL_DAYS} Tage verlängern.`}
      </p>
      {message && <div className={`alert ${message.ok ? 'alert--ok' : 'alert--error'}`} role={message.ok ? 'status' : 'alert'}>{message.text}</div>}
      <div className="form-actions">
        <button className="btn btn--secondary" type="button" disabled={tenant.trialExtended || extend.isPending} onClick={() => extend.mutate()}>
          {extend.isPending ? 'Wird verlängert …' : `Um ${TRIAL_DAYS} Tage verlängern`}
        </button>
      </div>
    </section>
  );
}

function OwnerCard({ tenant }: { tenant: TenantSummary }) {
  const client = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('owner');
  const [sendMail, setSendMail] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<{ invitation: InvitationResult; mail: boolean } | null>(null);

  const invite = useMutation({
    mutationFn: () => operatorApi<{ data: InvitationResult }>('POST', `/tenants/${tenant.id}/owner-invitation`, { name: name.trim(), email: email.trim(), roleKey, sendMail }),
    onSuccess: (res) => {
      setResult({ invitation: res.data, mail: sendMail });
      setName('');
      setEmail('');
      setErrors({});
      client.invalidateQueries({ queryKey: ['operator-tenant', tenant.id] });
      client.invalidateQueries({ queryKey: ['operator-tenants'] });
      client.invalidateQueries({ queryKey: ['operator-log', tenant.id] });
    },
    onError: (e) => {
      setErrors(e instanceof ApiError && e.status === 422 ? e.fields : {});
      setFormError(e instanceof ApiError ? (e.status === 422 ? 'Bitte prüfen Sie Ihre Eingaben.' : e.message) : 'Die Einladung konnte nicht angelegt werden.');
    },
  });
  const error = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  return (
    <>
      {result && <InvitationLink result={result.invitation} mailRequested={result.mail} onClose={() => setResult(null)} />}
      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          invite.mutate();
        }}
        noValidate
      >
        <h2>Benutzer einladen</h2>
        <p className="muted small">Für den ersten Inhaber eines neuen Mandanten. Er bekommt einen Link, über den er sein Passwort selbst festlegt. Die Plätze der Lizenz gelten ({tenant.seatsUsed} von {tenant.maxUsers} belegt).</p>
        {formError && <div className="alert alert--error" role="alert">{formError}</div>}
        <div className="form-grid">
          <label className="field">
            <span>Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" aria-invalid={!!errors.name} />
            {error('name')}
          </label>
          <label className="field">
            <span>E-Mail</span>
            <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" aria-invalid={!!errors.email} />
            {error('email')}
          </label>
          <label className="field">
            <span>Rolle</span>
            <select className="input" value={roleKey} onChange={(e) => setRoleKey(e.target.value)}>
              {DEFAULT_ROLES.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
            {error('roleKey')}
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={sendMail} onChange={(e) => setSendMail(e.target.checked)} />
          <span>Einladung zusätzlich per E-Mail schicken</span>
        </label>
        <div className="form-actions">
          <button className="btn btn--primary" type="submit" disabled={invite.isPending || name.trim().length < 2 || !email.trim()}>{invite.isPending ? 'Wird angelegt …' : 'Einladung anlegen'}</button>
        </div>
      </form>
    </>
  );
}

function ApiKeysCard({ tenantId }: { tenantId: string }) {
  const client = useQueryClient();
  const keys = useQuery({ queryKey: ['operator-keys', tenantId], queryFn: async () => (await operatorApi<{ data: OperatorApiKey[] }>('GET', `/tenants/${tenantId}/api-keys`)).data });
  const remove = useMutation({
    mutationFn: (keyId: string) => operatorApi('DELETE', `/tenants/${tenantId}/api-keys/${keyId}`),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['operator-keys', tenantId] });
      client.invalidateQueries({ queryKey: ['operator-log', tenantId] });
    },
  });
  const removeError = remove.error instanceof ApiError ? remove.error.message : null;

  return (
    <section className="card">
      <h2>API-Schlüssel</h2>
      <p className="muted small">
        Schlüssel für Anfrageformular, Chat und Newsletter-Anmeldung legt der Mandant selbst an (Einrichtung → APIs und Webhooks), sobald das Modul in seinem Paket enthalten ist oder als Zusatzmodul freigeschaltet wurde.
        Hier sehen Sie, welche Schlüssel es gibt, und löschen veraltete oder nicht mehr gewünschte.
      </p>
      {removeError && <div className="alert alert--error" role="alert">{removeError}</div>}

      <table className="table table--cards">
        <thead>
          <tr>
            <th>Name</th>
            <th>Rechte</th>
            <th>Angelegt</th>
            <th>Zuletzt benutzt</th>
            <th><span className="sr-only">Aktionen</span></th>
          </tr>
        </thead>
        <tbody>
          {keys.isLoading && <tr><td colSpan={5} className="muted">Wird geladen …</td></tr>}
          {keys.data?.length === 0 && <tr><td colSpan={5} className="empty">Noch kein Schlüssel angelegt.</td></tr>}
          {keys.data?.map((k) => (
            <tr key={k.id}>
              <td>
                {k.name}
                {k.revokedAt && <span className="badge badge--rejected"> Gesperrt</span>}
                <div className="muted small">ktk_{k.prefix}_…</div>
              </td>
              <td data-label="Rechte">{k.scopes.map((s) => API_SCOPES.find((x) => x.key === s)?.label ?? s).join(', ')}</td>
              <td data-label="Angelegt">{stamp.format(new Date(k.createdAt))}</td>
              <td data-label="Zuletzt benutzt">{k.lastUsedAt ? stamp.format(new Date(k.lastUsedAt)) : <span className="muted">noch nie</span>}</td>
              <td className="cell-actions">
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Schlüssel „${k.name}“ löschen?\n\nDie Webseite, die ihn benutzt, kann danach nichts mehr an Wirkia senden. Das lässt sich nicht rückgängig machen.`)) remove.mutate(k.id);
                  }}
                >
                  Löschen
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function LogCard({ tenantId }: { tenantId: string }) {
  const log = useQuery({ queryKey: ['operator-log', tenantId], queryFn: async () => (await operatorApi<{ data: OperatorLogEntry[] }>('GET', `/tenants/${tenantId}/log`)).data });
  return (
    <CollapsibleCard title="Protokoll" storageKey="operator-log">
      <p className="muted small">Was Betreiber an diesem Mandanten geändert haben, die letzten 50 Einträge.</p>
      {log.isLoading && <p className="muted">Wird geladen …</p>}
      {log.data?.length === 0 && <p className="muted">Noch keine Einträge.</p>}
      <ul className="history-list">
        {log.data?.map((e) => (
          <li key={e.id}>
            <span className="history-list__when">{stamp.format(new Date(e.at))} Uhr</span>
            <span>
              <strong>{e.operatorName ?? 'Unbekannt'}</strong> {e.description}
            </span>
          </li>
        ))}
      </ul>
    </CollapsibleCard>
  );
}
